import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Presentation } from "lucide-react";
import { toast } from "sonner";
import { BrandWordmark } from "@/components/BrandWordmark";
import { BrandLogoMark } from "@/components/BrandLogoMark";

/**
 * v1.4 "815 Digital Services" rebrand — a premium split layout: a
 * champagne brand panel (left, desktop-only) establishes 815 Digital
 * Services as the business behind the platform and frames DriveDesk Hub
 * as its featured demonstration, while the sign-in form (right) keeps
 * every existing field/testid untouched. On mobile, the brand panel
 * collapses into a compact header above the form so nothing is lost on
 * small screens.
 */
export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      const dest = location.state?.from?.pathname || "/";
      navigate(dest, { replace: true });
      toast.success("Welcome back!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row" data-testid="login-page">
      {/* Brand panel — desktop only. Champagne surface with a very low-
          opacity art-deco sunburst watermark, per design_guidelines.md
          "login_page". */}
      <div className="hidden lg:flex lg:w-[44%] relative overflow-hidden bg-[hsl(var(--champagne))] items-center justify-center px-12">
        <svg
          className="absolute inset-0 h-full w-full opacity-[0.06]"
          viewBox="0 0 400 400"
          fill="none"
          aria-hidden="true"
        >
          {Array.from({ length: 24 }).map((_, i) => (
            <line
              key={i}
              x1="200"
              y1="200"
              x2={200 + 260 * Math.cos((i * Math.PI) / 12)}
              y2={200 + 260 * Math.sin((i * Math.PI) / 12)}
              stroke="hsl(var(--gold))"
              strokeWidth="1.5"
            />
          ))}
          <circle cx="200" cy="200" r="120" stroke="hsl(var(--gold))" strokeWidth="1.5" />
          <circle cx="200" cy="200" r="150" stroke="hsl(var(--gold))" strokeWidth="1" />
        </svg>

        <div className="relative z-10 max-w-sm text-center space-y-6">
          <BrandLogoMark className="mx-auto" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[hsl(var(--gold))]">
              815 Digital Services
            </p>
            <h2 className="mt-2 font-heading text-2xl font-semibold text-foreground">
              Professional Business Systems
            </h2>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              DriveDesk Hub is our featured demonstration — a complete operations platform built for
              driving schools, showcasing what a custom-built system from 815 Digital Services can do
              for your business.
            </p>
          </div>
        </div>
      </div>

      {/* Sign-in form panel */}
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md space-y-8">
          <div className="flex flex-col items-center gap-3 text-center lg:items-start lg:text-left">
            <BrandLogoMark size="sm" className="lg:hidden" />
            <div>
              <p className="hidden lg:block text-xs font-semibold uppercase tracking-[0.18em] text-[hsl(var(--gold))] mb-1">
                815 Digital Services
              </p>
              <h1 className="font-heading text-2xl font-semibold">DriveDesk Hub</h1>
              <p className="text-sm text-muted-foreground mt-1">Sign in to manage your driving school</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="bg-card border rounded-[var(--radius)] p-6 sm:p-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="login-email">Email address</Label>
              <Input
                id="login-email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-11"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="login-password">Password</Label>
              <Input
                id="login-password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="********"
                className="h-11"
                data-testid="login-password-input"
              />
            </div>
            <Button type="submit" className="w-full h-11" disabled={loading} data-testid="login-submit-button">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Sign in
            </Button>
            <p className="text-sm text-center text-muted-foreground">
              New parent?{" "}
              <Link to="/register" className="text-[hsl(var(--role-admin))] font-medium" data-testid="login-register-link">
                Create an account
              </Link>
            </p>
          </form>
          <Link to="/demo" data-testid="experience-demo-link">
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 border-[hsl(var(--presentation))]/40 text-[hsl(var(--presentation))] hover:bg-[hsl(var(--presentation-soft))]"
              data-testid="experience-demo-button"
            >
              <Presentation className="h-4 w-4 mr-2" /> Experience the Demo
            </Button>
          </Link>
          <p className="text-xs text-center text-muted-foreground">
            Staff & Instructor accounts are created by your administrator.
          </p>
          <BrandWordmark className="pt-2" />
        </div>
      </div>
    </div>
  );
}
